import api from './api';

export type DiffStatus = 'dev_only' | 'prod_only' | 'different' | 'identical';

export interface RecordRef {
  id: number;
  [k: string]: any;
}

export interface RecordDiff {
  status: DiffStatus;
  natural_key: string;
  dev_record: RecordRef | null;
  prod_record: RecordRef | null;
  diff_fields: string[];
}

export interface TableDiffResponse {
  table: string;
  records: RecordDiff[];
}

export interface TableSummary {
  table: string;
  dev_only: number;
  prod_only: number;
  different: number;
  identical: number;
  error?: string | null;
}

export interface DiffSummaryResponse {
  dev_url: string;
  tables: TableSummary[];
}

export type SyncDirection = 'dev_to_prod' | 'prod_to_dev';

export interface SyncRequest {
  table: string;
  ids: number[];
  direction?: SyncDirection;
}

export interface SyncResultItem {
  natural_key: string;
  action: 'created' | 'updated' | 'skipped' | 'error';
  success: boolean;
  error: string | null;
}

export interface SyncResponse {
  table: string;
  results: SyncResultItem[];
}

export const CONFIG_TABLES = [
  'meeting_types', 'services', 'clinics', 'referrals', 'summary_types', 'file_types', 'patient_status_configs',
  'patient_types', 'task_rules', 'medications', 'prompts',
  'diagnosis_types', 'wellness_configs', 'wellness_items', 'wellness_tips', 'questionnaire_types',
  'patient_field_configs',
  'email_templates', 'sms_templates', 'patient_notifications',
] as const;

export type ConfigTable = typeof CONFIG_TABLES[number];

export const TABLE_LABELS: Record<ConfigTable, string> = {
  meeting_types: 'Meeting Types',
  services: 'Services',
  clinics: 'Clinics',
  referrals: 'Referrals',
  summary_types: 'Summary Types',
  file_types: 'File Types',
  patient_status_configs: 'Patient Statuses',
  patient_types: 'Patient Types',
  task_rules: 'Task Rules',
  medications: 'Medications',
  prompts: 'Prompts',
  diagnosis_types: 'Diagnosis Types',
  wellness_configs: 'Wellness Configs',
  wellness_items: 'Wellness Items',
  wellness_tips: 'Wellness Tips',
  questionnaire_types: 'Questionnaire Types',
  patient_field_configs: 'Patient Field Configs',
  email_templates: 'Email Templates',
  sms_templates: 'SMS Templates',
  patient_notifications: 'Patient Notifications',
};

export interface ConfigSyncStatus {
  available: boolean;
  dev_url: string | null;
}

export interface InitializeMappingTableReport {
  table: string;
  mapped: number;
  dev_only: number;
  prod_only: number;
  ambiguous: number;
}

export interface InitializeMappingResponse {
  tables: InitializeMappingTableReport[];
}

export const configSyncService = {
  async getStatus(): Promise<ConfigSyncStatus> {
    const response = await api.get('/config-sync/status');
    return response.data;
  },

  async getDiffSummary(): Promise<DiffSummaryResponse> {
    const response = await api.get('/config-sync/diff');
    return response.data;
  },

  async getTableDiff(table: ConfigTable): Promise<TableDiffResponse> {
    const response = await api.get('/config-sync/diff', { params: { table } });
    return response.data;
  },

  async syncRecords(request: SyncRequest): Promise<SyncResponse> {
    const response = await api.post('/config-sync/sync', request);
    return response.data;
  },

  async initializeMapping(table?: string): Promise<InitializeMappingResponse> {
    const response = await api.post('/config-sync/initialize-mapping', table ? { table } : {});
    return response.data;
  },
};
